import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { AvailabilityRule, Slot } from '~/types/telemedicine'

dayjs.extend(utc)
dayjs.extend(timezone)

type Window = Pick<AvailabilityRule, 'weekday' | 'start_time' | 'end_time'>

export const WEEKDAYS_MONDAY_FIRST: { value: number, label: string, short: string }[] = [
    { value: 1, label: 'Monday', short: 'Mon' },
    { value: 2, label: 'Tuesday', short: 'Tue' },
    { value: 3, label: 'Wednesday', short: 'Wed' },
    { value: 4, label: 'Thursday', short: 'Thu' },
    { value: 5, label: 'Friday', short: 'Fri' },
    { value: 6, label: 'Saturday', short: 'Sat' },
    { value: 0, label: 'Sunday', short: 'Sun' },
]

const toMinutes = (time: string): number => {
    const [h, m] = time.split(':')
    return Number(h) * 60 + Number(m)
}

export function formatWindowTime(time: string): string {
    return toMinutes(time) >= 1440 ? 'Midnight' : time.slice(0, 5)
}

export function windowTouchesAnother(rule: AvailabilityRule, rules: AvailabilityRule[]): boolean {
    return rules.some(other => other.id !== rule.id && other.weekday === rule.weekday
        && (toMinutes(other.end_time) === toMinutes(rule.start_time) || toMinutes(other.start_time) === toMinutes(rule.end_time)))
}

export function rulesOverlap(a: Window, b: Window): boolean {
    if (a.weekday !== b.weekday) return false
    return toMinutes(a.start_time) < toMinutes(b.end_time) && toMinutes(b.start_time) < toMinutes(a.end_time)
}

const OVERLAP_MESSAGE = 'Overlaps another window on this day'
const ORDER_MESSAGE = 'End time must be after start time'

export function scheduleErrorMessage(error: { code?: string, message: string }): string {
    if (error.code === '23P01') return OVERLAP_MESSAGE
    if (error.code === '23514') return ORDER_MESSAGE
    return error.message
}

export function validateRule(rule: Window, existing: AvailabilityRule[]): string | null {
    if (toMinutes(rule.end_time) <= toMinutes(rule.start_time)) return ORDER_MESSAGE
    if (existing.some(other => rulesOverlap(rule, other))) return OVERLAP_MESSAGE
    return null
}

export function slotsPerWindow(start: string, end: string, durationMin: number, bufferMin: number): number {
    const step = durationMin + bufferMin
    if (step <= 0) return 0
    const length = toMinutes(end) - toMinutes(start)
    if (length < durationMin) return 0
    return Math.floor((length - durationMin) / step) + 1
}

export function groupSlotsByDay(slots: Slot[], tz: string): { day: string, label: string, slots: Slot[] }[] {
    const groups = new Map<string, { day: string, label: string, slots: Slot[] }>()
    const sorted = [...slots].sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
    for (const slot of sorted) {
        const local = dayjs(slot.starts_at).tz(tz)
        const day = local.format('YYYY-MM-DD')
        let group = groups.get(day)
        if (!group) {
            group = { day, label: local.format('ddd D MMM'), slots: [] }
            groups.set(day, group)
        }
        group.slots.push(slot)
    }
    return [...groups.values()].sort((a, b) => a.day.localeCompare(b.day))
}

export function localInputToIso(value: string, tz: string): string | null {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null
    const local = dayjs.tz(value, tz)
    return local.isValid() ? local.toISOString() : null
}

export function previewRange(now: Date, tz: string, days: number): { from: string, to: string } {
    const lastDay = dayjs.utc(dayjs(now).tz(tz).format('YYYY-MM-DD')).add(days, 'day').format('YYYY-MM-DD')
    return { from: now.toISOString(), to: dayjs.tz(`${lastDay}T00:00`, tz).toISOString() }
}

export interface PreviewDay {
    day: string
    weekday: string
    date: string
    today: boolean
    slots: Slot[]
}

export function previewWeeks(now: Date, tz: string, days: number, slots: Slot[]): (PreviewDay | null)[][] {
    const byDay = new Map(groupSlotsByDay(slots, tz).map(g => [g.day, g.slots]))
    const first = dayjs.utc(dayjs(now).tz(tz).format('YYYY-MM-DD'))
    const cells: (PreviewDay | null)[] = Array.from({ length: (first.day() + 6) % 7 }, () => null)
    for (let i = 0; i < days; i++) {
        const date = first.add(i, 'day')
        const day = date.format('YYYY-MM-DD')
        cells.push({ day, weekday: date.format('ddd'), date: date.format('D MMM'), today: i === 0, slots: byDay.get(day) ?? [] })
    }
    while (cells.length % 7) cells.push(null)
    return Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7))
}

export function formatExceptionRange(startsAt: string, endsAt: string, tz: string): string {
    const start = dayjs(startsAt).tz(tz)
    const end = dayjs(endsAt).tz(tz)
    const startDay = start.format('YYYY-MM-DD')
    const endsAtMidnight = end.format('HH:mm') === '00:00'
    const lastDay = dayjs.utc(end.format('YYYY-MM-DD')).subtract(endsAtMidnight ? 1 : 0, 'day')
    const dayLabel = (d: dayjs.Dayjs) => d.format('ddd D MMM')

    if (start.format('HH:mm') === '00:00' && endsAtMidnight) {
        return lastDay.format('YYYY-MM-DD') === startDay
            ? `${dayLabel(start)} · All day`
            : `${dayLabel(start)} – ${dayLabel(lastDay)}`
    }
    if (lastDay.format('YYYY-MM-DD') === startDay) {
        return `${dayLabel(start)} · ${start.format('HH:mm')} – ${endsAtMidnight ? 'Midnight' : end.format('HH:mm')}`
    }
    return `${dayLabel(start)}, ${start.format('HH:mm')} – ${dayLabel(end)}, ${end.format('HH:mm')}`
}
